import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BuilderLayout } from '@/components/layout/BuilderLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Plus,
  Trash2,
  Settings2,
  Sparkles,
  MessageSquare,
  ChevronRight,
  X,
  LayoutGrid,
  Palette,
  Wallet,
  Send,
  FileText,
  Clock,
  Settings,
  ChevronLeft,
  Loader2,
  Eye as EyeIcon,
  Smartphone,
  Monitor,
  Upload,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors, 
  DragEndEvent 
} from '@dnd-kit/core';
import { 
  arrayMove, 
  SortableContext, 
  sortableKeyboardCoordinates, 
  verticalListSortingStrategy 
} from '@dnd-kit/sortable';
import { DraggableFormField } from '@/components/DraggableFormField';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { generateForm } from '@/services/aiFormService';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { Helmet } from 'react-helmet-async';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';

interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'number' | 'date' | 'file';
  label: string;
  placeholder: string;
  required: boolean;
  options?: string[];
}

const FIELD_TYPES = [
  { id: 'text', label: 'Short Text', icon: FileText },
  { id: 'textarea', label: 'Long Text', icon: MessageSquare },
  { id: 'email', label: 'Email Address', icon: FileText },
  { id: 'phone', label: 'Phone Number', icon: FileText },
  { id: 'number', label: 'Number', icon: FileText },
  { id: 'select', label: 'Dropdown', icon: LayoutGrid },
  { id: 'date', label: 'Date Picker', icon: Clock },
  { id: 'file', label: 'File Upload', icon: Plus },
];

const FormBuilder = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isEditing = Boolean(formId);

  // State
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSettings, setFormSettings] = useState({
    primaryColor: '#00daf3',
    backgroundColor: 'transparent',
    borderRadius: '1rem',
    buttonText: 'Submit Response',
    whatsappNumber: '',
  });
  const [fields, setFields] = useState<FormField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [aiIntent, setAiIntent] = useState('');
  const [activeSidebar, setActiveSidebar] = useState<'elements' | 'settings' | 'ai' | 'style' | 'preview'>('elements');
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [mobileShowCanvas, setMobileShowCanvas] = useState(false);

  // Autosave: localStorage draft key per form (or "new" for unsaved)
  const draftKey = `form-draft:${formId ?? 'new'}`;
  const draftLoadedRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // Load form data + restore any local draft
  useEffect(() => {
    const loadForm = async () => {
      // Existing form: fetch from DB first
      if (isEditing && formId && user) {
        try {
          const { data: form, error } = await supabase
            .from('forms')
            .select('*')
            .eq('id', formId)
            .eq('user_id', user.id)
            .maybeSingle();

          if (error || !form) throw error || new Error('Form not found');

          setFormTitle(form.title);
          setFormDescription(form.description || '');
          setFields(Array.isArray(form.fields) ? (form.fields as unknown as FormField[]) : []);
        } catch (err) {
          console.error(err);
          toast({ title: "Error loading form", variant: "destructive" });
          navigate('/dashboard');
          return;
        }
      }

      // Restore local draft (overrides DB if newer draft exists)
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft.formTitle !== undefined) setFormTitle(draft.formTitle);
          if (draft.formDescription !== undefined) setFormDescription(draft.formDescription);
          if (Array.isArray(draft.fields)) setFields(draft.fields);
          if (draft.formSettings) setFormSettings((s) => ({ ...s, ...draft.formSettings }));
          toast({ title: 'Draft restored', description: 'Recovered your unsaved changes.' });
        }
      } catch (e) {
        console.warn('Draft restore failed', e);
      }

      initialLoadDoneRef.current = true;
      draftLoadedRef.current = true;
    };
    loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, isEditing, user]);

  // Debounced autosave to localStorage
  useEffect(() => {
    if (!initialLoadDoneRef.current) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ formTitle, formDescription, fields, formSettings, savedAt: Date.now() })
        );
      } catch (e) {
        console.warn('Autosave failed', e);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [formTitle, formDescription, fields, formSettings, draftKey]);


  // Handlers
  const addField = (type: FormField['type']) => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      type,
      label: `New ${type} field`,
      placeholder: `Enter ${type}...`,
      required: false,
      options: ['select', 'radio', 'checkbox'].includes(type) ? ['Option 1', 'Option 2'] : undefined
    };
    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
    setActiveSidebar('settings');
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const removeField = (id: string) => {
    setFields(fields.filter(f => f.id !== id));
    if (selectedFieldId === id) setSelectedFieldId(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async (publish: boolean) => {
    if (!user) return;
    setIsLoading(true);
    try {
      const formData: any = {
        user_id: user.id,
        title: formTitle || 'Untitled Form',
        description: formDescription,
        fields: fields as any,
        is_published: publish,
      };

      if (isEditing) {
        await supabase.from('forms').update(formData).eq('id', formId);
        try { localStorage.removeItem(draftKey); } catch {}
      } else {
        const { data } = await supabase.from('forms').insert(formData).select('id').single();
        if (data) {
          try {
            localStorage.removeItem(draftKey);
            localStorage.removeItem('form-draft:new');
          } catch {}
          navigate(`/form-builder/${data.id}`, { replace: true });
        }
      }
      toast({ title: publish ? "Form published!" : "Draft saved!" });
    } catch (err) {
      console.error(err);
      toast({ title: "Error saving form", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAiRequest = async () => {
    if (!aiIntent.trim()) return;
    setIsAiGenerating(true);
    try {
      const currentForm = {
        title: formTitle,
        description: formDescription,
        fields: fields
      };
      
      const result = await generateForm(aiIntent, currentForm as any);
      
      if (result) {
        setFormTitle(result.title);
        setFormDescription(result.description);
        setFields(Array.isArray(result.fields) ? (result.fields as FormField[]) : []);
        setAiIntent('');
        toast({ title: "AI update applied!" });
      }
    } catch (err: any) {
      console.error('AI Error:', err);
      toast({ 
        title: "AI Architect error", 
        description: err.message || "Something went wrong.", 
        variant: "destructive" 
      });
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Redesign: Components for the Builder
  const ElementsSidebar = () => (
    <div className="w-full sm:w-80 bg-surface-lowest border-r border-border/10 flex flex-col shrink-0 animate-in slide-in-from-left duration-500">
      <div className="p-4 sm:p-8 pb-4 flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">Build</p>
          <h3 className="text-xl font-display font-bold tracking-tight">Elements</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileShowCanvas(true)} className="sm:hidden rounded-full h-8 w-8 hover:bg-surface-high">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 space-y-8">
        <div className="grid grid-cols-2 gap-3">
          {FIELD_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => addField(type.id as FormField['type'])}
              className="flex flex-col items-center justify-center p-4 rounded-2xl bg-surface-low hover:bg-surface-high transition-all group border border-border/5"
            >
               <type.icon className="h-5 w-5 mb-2 text-muted-foreground group-hover:text-primary transition-colors" />
               <span className="text-[10px] font-bold text-muted-foreground group-hover:text-foreground uppercase tracking-wider">{type.label}</span>
            </button>
          ))}
        </div>

        <div className="space-y-4 pt-4">
           <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Metadata</p>
           <div className="space-y-4">
              <div className="space-y-2">
                 <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Form Title</Label>
                 <Input 
                   value={formTitle} 
                   onChange={(e) => setFormTitle(e.target.value)}
                   className="bg-surface-low border-none h-11 rounded-xl focus:ring-1 focus:ring-primary/20"
                   placeholder="Enter title..."
                 />
              </div>
              <div className="space-y-2">
                 <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Description</Label>
                 <Textarea 
                   value={formDescription} 
                   onChange={(e) => setFormDescription(e.target.value)}
                   className="bg-surface-low border-none min-h-[100px] rounded-xl focus:ring-1 focus:ring-primary/20 resize-none"
                   placeholder="Form purpose..."
                 />
              </div>
           </div>
        </div>
      </div>
    </div>
  );

  const SettingsSidebar = () => {
    const field = fields.find(f => f.id === selectedFieldId);
    if (!field) return ElementsSidebar();

    return (
      <div className="w-full sm:w-80 bg-surface-lowest border-r border-border/10 flex flex-col shrink-0 animate-in slide-in-from-left duration-500">
        <div className="p-4 sm:p-8 pb-4 flex items-center justify-between">
           <div>
              <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">Configure</p>
              <h3 className="text-xl font-display font-bold tracking-tight">Properties</h3>
           </div>
           <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => setSelectedFieldId(null)} className="rounded-full h-8 w-8 hover:bg-surface-high">
                 <X className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setMobileShowCanvas(true)} className="sm:hidden rounded-full h-8 w-8 hover:bg-surface-high">
                 <ChevronRight className="h-4 w-4" />
              </Button>
           </div>
        </div>
        
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 space-y-6">
           <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Label</Label>
              <Input 
                value={field.label} 
                onChange={(e) => updateField(field.id, { label: e.target.value })}
                className="bg-surface-low border-none h-11 rounded-xl"
              />
           </div>
           <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Placeholder</Label>
              <Input 
                value={field.placeholder} 
                onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                className="bg-surface-low border-none h-11 rounded-xl"
              />
           </div>
           
           <div className="flex items-center justify-between p-5 rounded-2xl bg-surface-low border border-border/5">
              <div className="space-y-0.5">
                 <Label className="text-xs font-bold">Required Field</Label>
                 <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Mandatory input</p>
              </div>
              <Switch 
                checked={field.required}
                onCheckedChange={(checked) => updateField(field.id, { required: checked })}
              />
           </div>

           {['select', 'radio', 'checkbox'].includes(field.type) && (
             <div className="space-y-4">
                <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Options</Label>
                <div className="space-y-2">
                   {field.options?.map((opt, i) => (
                     <div key={i} className="flex gap-2">
                        <Input 
                          value={opt} 
                          onChange={(e) => {
                            const newOpts = [...(field.options || [])];
                            newOpts[i] = e.target.value;
                            updateField(field.id, { options: newOpts });
                          }}
                          className="bg-surface-low border-none h-10 rounded-xl"
                        />
                        <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0 hover:bg-destructive/10 hover:text-destructive" onClick={() => {
                          updateField(field.id, { options: field.options?.filter((_, idx) => idx !== i) });
                        }}>
                           <Trash2 className="h-4 w-4" />
                        </Button>
                     </div>
                   ))}
                   <Button variant="outline" size="sm" className="w-full h-10 rounded-xl gap-2 border-border/10 bg-transparent hover:bg-surface-high" onClick={() => {
                     updateField(field.id, { options: [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`] });
                   }}>
                      <Plus className="h-4 w-4" />
                      Add Option
                   </Button>
                </div>
             </div>
           )}
        </div>
      </div>
    );
  };

  const GlobalSettingsSidebar = () => (
    <div className="w-full sm:w-80 bg-surface-lowest border-r border-border/10 flex flex-col shrink-0 animate-in slide-in-from-left duration-500">
      <div className="p-4 sm:p-8 pb-4 flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">Configure</p>
          <h3 className="text-xl font-display font-bold tracking-tight">Global Settings</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileShowCanvas(true)} className="sm:hidden rounded-full h-8 w-8 hover:bg-surface-high">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 space-y-8">
        <div className="space-y-4">
           <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Delivery</Label>
           <div className="space-y-2">
              <Label className="text-xs">WhatsApp Number</Label>
              <Input 
                value={formSettings.whatsappNumber} 
                onChange={(e) => setFormSettings({...formSettings, whatsappNumber: e.target.value})}
                placeholder="+1234567890"
                className="bg-surface-low border-none h-11 rounded-xl"
              />
           </div>
        </div>
        <div className="space-y-4">
           <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Submission</Label>
           <div className="space-y-2">
              <Label className="text-xs">Button Text</Label>
              <Input 
                value={formSettings.buttonText} 
                onChange={(e) => setFormSettings({...formSettings, buttonText: e.target.value})}
                placeholder="Submit Response"
                className="bg-surface-low border-none h-11 rounded-xl"
              />
           </div>
        </div>
      </div>
    </div>
  );

  const StyleSidebar = () => (
    <div className="w-full sm:w-80 bg-surface-lowest border-r border-border/10 flex flex-col shrink-0 animate-in slide-in-from-left duration-500">
      <div className="p-4 sm:p-8 pb-4 flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">Appearance</p>
          <h3 className="text-xl font-display font-bold tracking-tight">Design System</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileShowCanvas(true)} className="sm:hidden rounded-full h-8 w-8 hover:bg-surface-high">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 space-y-8">
        <div className="space-y-4">
           <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Accent Color</Label>
           <div className="flex gap-2">
              {['#00daf3', '#6366f1', '#f43f5e', '#10b981', '#f59e0b'].map(color => (
                <button
                  key={color}
                  onClick={() => setFormSettings({...formSettings, primaryColor: color})}
                  className={cn(
                    "h-8 w-8 rounded-full border-2 transition-all",
                    formSettings.primaryColor === color ? "border-foreground scale-110 shadow-lg shadow-primary/20" : "border-transparent"
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
           </div>
        </div>
        <div className="space-y-4">
           <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Border Radius</Label>
           <div className="grid grid-cols-4 gap-2">
              {['0rem', '0.5rem', '1rem', '2rem'].map(radius => (
                <button
                  key={radius}
                  onClick={() => setFormSettings({...formSettings, borderRadius: radius})}
                  className={cn(
                    "h-10 rounded-lg bg-surface-low border border-border/5 text-[10px] font-bold uppercase transition-all",
                    formSettings.borderRadius === radius ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  )}
                >
                  {radius === '0rem' ? 'None' : radius === '2rem' ? 'Full' : radius}
                </button>
              ))}
           </div>
        </div>
      </div>
    </div>
  );

  const AIChatSidebar = () => (
    <div className="w-full sm:w-80 bg-surface-lowest border-r border-border/10 flex flex-col shrink-0 animate-in slide-in-from-left duration-500">
      <div className="p-4 sm:p-8 pb-4 flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2">Architect</p>
          <h3 className="text-xl font-display font-bold tracking-tight flex items-center gap-2">
             AI Sidekick
             <Sparkles className="h-4 w-4 text-primary animate-pulse" />
          </h3>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileShowCanvas(true)} className="sm:hidden rounded-full h-8 w-8 hover:bg-surface-high">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
         <div className="flex-1 p-4 sm:p-8 overflow-y-auto space-y-4">
            <div className="bg-surface-low rounded-2xl p-5 text-sm text-muted-foreground leading-relaxed">
               I'm your Digital Architect. Describe the form you need, and I'll orchestrate the components for you.
            </div>
         </div>
         <div className="p-4 sm:p-8 border-t border-border/5">
            <div className="relative">
               <Textarea 
                 value={aiIntent}
                 onChange={(e) => setAiIntent(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && !e.shiftKey) {
                     e.preventDefault();
                     handleAiRequest();
                   }
                 }}
                 disabled={isAiGenerating}
                 placeholder="Command your architect..."
                 className="min-h-[120px] w-full rounded-2xl bg-surface-low border-none p-5 pr-12 text-sm focus:ring-1 focus:ring-primary/20 resize-none shadow-inner"
               />
               <Button 
                 size="icon" 
                 onClick={handleAiRequest}
                 disabled={isAiGenerating || !aiIntent.trim()}
                 className="absolute bottom-4 right-4 h-9 w-9 rounded-xl btn-gradient"
               >
                  {isAiGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
               </Button>
            </div>
         </div>
      </div>
    </div>
  );

  const PreviewSidebar = () => {
    const frameWidth = previewDevice === 'mobile' ? 'max-w-sm' : 'max-w-2xl';
    const radius = formSettings.borderRadius;
    const accent = formSettings.primaryColor;
    return (
      <div className="w-full sm:w-[440px] bg-surface-lowest border-r border-border/10 flex flex-col shrink-0 animate-in slide-in-from-left duration-500">
        <div className="p-4 sm:p-6 pb-4 flex items-center justify-between border-b border-border/5">
          <div>
            <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1">Live</p>
            <h3 className="text-lg font-display font-bold tracking-tight">Preview</h3>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex items-center bg-surface-low rounded-full p-1">
              <button
                onClick={() => setPreviewDevice('mobile')}
                className={cn("h-7 w-7 rounded-full flex items-center justify-center transition-all", previewDevice === 'mobile' ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
                aria-label="Mobile preview"
              >
                <Smartphone className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setPreviewDevice('desktop')}
                className={cn("h-7 w-7 rounded-full flex items-center justify-center transition-all", previewDevice === 'desktop' ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
                aria-label="Desktop preview"
              >
                <Monitor className="h-3.5 w-3.5" />
              </button>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setMobileShowCanvas(true)} className="sm:hidden rounded-full h-8 w-8 hover:bg-surface-high">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-surface-lowest">
          <div className={cn("mx-auto w-full transition-all", frameWidth)}>
            <div className="bg-background border border-border/10 shadow-2xl overflow-hidden" style={{ borderRadius: radius }}>
              <div className="p-6 sm:p-8 border-b border-border/5">
                <h2 className="text-xl sm:text-2xl font-display font-bold tracking-tight text-foreground">
                  {formTitle || 'Untitled Form'}
                </h2>
                {formDescription && (
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{formDescription}</p>
                )}
              </div>
              <div className="p-6 sm:p-8 space-y-6">
                {fields.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground italic">
                    Add fields to see them here.
                  </div>
                ) : (
                  fields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <label className="text-xs font-semibold text-foreground flex items-center gap-1">
                        {field.label}
                        {field.required && <span style={{ color: accent }}>*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <Textarea placeholder={field.placeholder} className="bg-surface-low border-border/10 min-h-[90px]" style={{ borderRadius: radius }} />
                      ) : field.type === 'select' ? (
                        <select className="w-full h-11 px-3 bg-surface-low border border-border/10 text-sm" style={{ borderRadius: radius }} defaultValue="">
                          <option value="" disabled>{field.placeholder || 'Select...'}</option>
                          {field.options?.map((o, i) => <option key={i} value={o}>{o}</option>)}
                        </select>
                      ) : field.type === 'radio' || field.type === 'checkbox' ? (
                        <div className="space-y-2">
                          {field.options?.map((o, i) => (
                            <label key={i} className="flex items-center gap-3 text-sm text-foreground cursor-pointer p-3 bg-surface-low border border-border/10" style={{ borderRadius: radius }}>
                              <input type={field.type} name={field.id} className="accent-primary" />
                              {o}
                            </label>
                          ))}
                        </div>
                      ) : field.type === 'file' ? (
                        <div className="h-20 border-2 border-dashed border-border/20 bg-surface-low flex flex-col items-center justify-center gap-1 text-muted-foreground text-xs" style={{ borderRadius: radius }}>
                          <Upload className="h-4 w-4" />
                          <span>Click to upload</span>
                        </div>
                      ) : field.type === 'date' ? (
                        <div className="relative">
                          <Input type="date" className="bg-surface-low border-border/10 h-11" style={{ borderRadius: radius }} />
                        </div>
                      ) : (
                        <Input
                          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                          placeholder={field.placeholder}
                          className="bg-surface-low border-border/10 h-11"
                          style={{ borderRadius: radius }}
                        />
                      )}
                    </div>
                  ))
                )}
                {fields.length > 0 && (
                  <button
                    onClick={() => toast({ title: 'Preview mode', description: 'Save and publish the form to accept real submissions.' })}
                    className="w-full h-12 text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: accent, borderRadius: radius }}
                  >
                    {formSettings.buttonText || 'Submit Response'}
                  </button>
                )}
              </div>
            </div>
            <p className="text-center text-[10px] text-muted-foreground/60 mt-4 uppercase tracking-widest">
              Live preview · reflects unsaved edits
            </p>
          </div>
        </div>
      </div>
    );
  };


  return (
    <BuilderLayout 
      formTitle={formTitle} 
      isSaving={isLoading}
      isPublished={isEditing && fields.length > 0}
      onSave={handleSave}
      onPreview={() => window.open(`/form/${formId}`, '_blank')}
      activeTab={activeSidebar}
      onTabChange={(tab: any) => { setActiveSidebar(tab); setMobileShowCanvas(false); }}
    >
      <Helmet>
        <title>{formTitle ? `${formTitle} | Architect` : 'Architect Mode'} | Bravax Forms</title>
      </Helmet>

      {/* Sidebars — hidden on mobile when user opens canvas */}
      <div className={cn("flex", mobileShowCanvas && "hidden sm:flex")}>
        {activeSidebar === 'elements' && (selectedFieldId ? SettingsSidebar() : ElementsSidebar())}
        {activeSidebar === 'settings' && GlobalSettingsSidebar()}
        {activeSidebar === 'ai' && AIChatSidebar()}
        {activeSidebar === 'style' && StyleSidebar()}
        {activeSidebar === 'preview' && PreviewSidebar()}
      </div>

      {/* Main Canvas — hidden on mobile when a sidebar is showing */}
      <div className={cn(
        "flex-1 overflow-y-auto flex-col items-center bg-[url('/grid.svg')] bg-center py-8 sm:py-20 px-4 sm:px-8",
        mobileShowCanvas ? "flex" : "hidden sm:flex"
      )}>
         <div className="w-full max-w-2xl space-y-6 sm:space-y-12">
            {/* Form Header on Canvas */}
            <div className="text-center space-y-3 animate-in fade-in slide-in-from-top-10 duration-1000">
               <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Workspace</span>
               <h2 className="text-3xl sm:text-5xl font-display font-bold tracking-tightest">
                  {formTitle || 'Start Orchestrating'}
               </h2>
               <p className="text-base sm:text-lg text-muted-foreground font-sans max-w-lg mx-auto leading-relaxed">
                  {formDescription || 'Your masterpiece begins here. Drag elements to the canvas or use the AI Sidekick.'}
               </p>
            </div>

            {/* Draggable Area */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-6">
                  {fields.length === 0 ? (
                    <div className="group relative py-16 sm:py-32 rounded-[2.5rem] bg-surface-low/30 border border-dashed border-border/20 flex flex-col items-center justify-center text-center px-6 sm:px-12 transition-all hover:bg-surface-low/50 overflow-hidden">
                       <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                       <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-3xl bg-surface-high flex items-center justify-center mb-6 sm:mb-8 relative z-10 shadow-2xl">
                          <Plus className="h-7 w-7 sm:h-10 sm:w-10 text-primary/40 group-hover:text-primary group-hover:scale-110 transition-all" />
                       </div>
                       <h3 className="text-xl sm:text-2xl font-display font-bold mb-3 relative z-10">Empty Workspace</h3>
                       <p className="text-sm sm:text-base text-muted-foreground max-w-xs mb-8 sm:mb-10 relative z-10">
                          Deploy your first component or let the AI Architect build the foundation for you.
                       </p>
                       <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 relative z-10 w-full sm:w-auto">
                          <Button variant="outline" className="rounded-2xl h-12 px-6 sm:px-8 border-border/10 bg-surface-low w-full sm:w-auto" onClick={() => addField('text')}>
                             Add Text Field
                          </Button>
                          <Button className="rounded-2xl h-12 px-6 sm:px-8 btn-gradient w-full sm:w-auto" onClick={() => { setActiveSidebar('ai'); setMobileShowCanvas(false); }}>
                             <Sparkles className="h-4 w-4 mr-2" />
                             Build with AI
                          </Button>
                       </div>
                    </div>
                  ) : (
                    fields.map((field) => (
                      <div 
                        key={field.id} 
                        onClick={() => { setSelectedFieldId(field.id); setActiveSidebar('settings'); }}
                        className={cn(
                          "transition-all duration-500",
                          selectedFieldId === field.id ? "scale-[1.02]" : "hover:scale-[1.01]"
                        )}
                      >
                        <DraggableFormField 
                          id={field.id}
                          field={field}
                          onUpdate={(updates) => updateField(field.id, updates)}
                          onRemove={() => removeField(field.id)}
                        />
                      </div>
                    ))
                  )}
                  
                  {fields.length > 0 && (
                    <div className="flex justify-center pt-10">
                       <Button 
                         variant="ghost" 
                         className="rounded-full h-16 w-16 bg-surface-high/50 backdrop-blur-xl border border-border/10 shadow-2xl hover:scale-110 transition-all hover:bg-primary hover:text-primary-foreground group"
                         onClick={() => addField('text')}
                       >
                          <Plus className="h-8 w-8 group-hover:rotate-90 transition-transform duration-500" />
                       </Button>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
         </div>
      </div>

      {/* Floating Control Hub (Right side) — hidden on mobile (uses bottom bar instead) */}
      <div className="hidden sm:flex w-20 bg-surface-lowest border-l border-border/10 flex-col items-center py-10 gap-8 shrink-0 relative z-50">
         {[
           { id: 'elements', icon: LayoutGrid, tooltip: 'Elements' },
           { id: 'ai', icon: Sparkles, tooltip: 'Architect' },
           { id: 'style', icon: Palette, tooltip: 'Styling' },
           { id: 'preview', icon: EyeIcon, tooltip: 'Preview' },
           { id: 'settings', icon: Settings, tooltip: 'General' }
         ].map((tool) => (
           <button
             key={tool.id}
             onClick={() => { setActiveSidebar(tool.id as any); setMobileShowCanvas(false); }}
             className={cn(
               "h-12 w-12 rounded-2xl flex items-center justify-center transition-all group relative",
               activeSidebar === tool.id ? "bg-primary text-primary-foreground shadow-[0_0_20px_rgba(0,218,243,0.3)]" : "text-muted-foreground hover:bg-surface-high hover:text-foreground"
             )}
           >
              <tool.icon className="h-5 w-5" />
              <div className="absolute right-full mr-4 px-3 py-1.5 rounded-lg bg-surface-highest text-foreground text-[10px] font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all pointer-events-none border border-border/10 shadow-2xl whitespace-nowrap translate-x-2 group-hover:translate-x-0">
                 {tool.tooltip}
              </div>
           </button>
         ))}
      </div>
    </BuilderLayout>
  );
};

export default FormBuilder;